import React, { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { FadeInImage } from '../common/FadeInImage';

interface OrganizerLogoProps {
  logoUrl: string | null;
  configLogoUrl: string;
  organizerName: string;
  isSuperAdminOrganizer?: boolean;
}

export const OrganizerLogo = React.memo(function OrganizerLogo({ logoUrl, configLogoUrl, organizerName, isSuperAdminOrganizer }: OrganizerLogoProps) {
  const [processedLogo, setProcessedLogo] = useState<string | null>(null);
  const [showTrophy, setShowTrophy] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!logoUrl || (!isSuperAdminOrganizer && logoUrl === configLogoUrl)) {
      setProcessedLogo(null);
      // Solo mostrar el trofeo si pasan 800ms (evita el flash durante la carga inicial)
      timeout = setTimeout(() => setShowTrophy(true), 800);
      return () => clearTimeout(timeout);
    }
    
    setShowTrophy(false);
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        // Chroma-keying out black (R,G,B < 35)
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] < 35 && data[i+1] < 35 && data[i+2] < 35) {
            data[i+3] = 0;
          }
        }
        ctx.putImageData(imageData, 0, 0);
        setProcessedLogo(canvas.toDataURL());
      }
    };
    img.onerror = () => {
      setProcessedLogo(logoUrl);
    };
  }, [logoUrl, configLogoUrl]);

  if (!logoUrl || (!isSuperAdminOrganizer && logoUrl === configLogoUrl)) {
    if (!showTrophy) {
      return (
        <div className="w-11 h-11 md:w-14 md:h-14 flex items-center justify-center">
          <div className="w-full h-full rounded-xl bg-white/5 animate-pulse" />
        </div>
      );
    }

    return (
      <div className="w-11 h-11 md:w-14 md:h-14 rounded-xl flex items-center justify-center theme-primary-bg text-black shadow-lg transition-transform group-hover:scale-105 duration-300">
        <Trophy size={18} />
      </div>
    );
  }

  // Si está procesando, no mostramos el trofeo, mostramos un espacio vacío o el FadeInImage con la URL original
  return (
    <div className="w-11 h-11 md:w-14 md:h-14 flex items-center justify-center">
      <FadeInImage 
        src={processedLogo || logoUrl} 
        alt={organizerName} 
        className="w-full h-full object-contain transition-transform group-hover:scale-105 duration-300" 
        referrerPolicy="no-referrer"
        noPlaceholderBg={true}
      />
    </div>
  );
});
